// Money / date formatting helpers. `currency` is a display symbol (e.g. '$',
// '€', '£', '¥', 'HK$', 'NT$') — no conversion, no exchange rate. Users pick
// the symbol they want to see in Settings.

export function formatMoney(amount, currency = '$') {
  const n = Number(amount) || 0;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const sym = currency || '$';
  // 2 decimals by default. JPY-style (no decimals) symbols are detected by
  // checking if the symbol is one of the common yen-like characters.
  const decimals = sym === '¥' || sym === '₩' ? 0 : 2;
  return `${sign}${sym}${abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function formatCompactMoney(amount, currency = '$') {
  const n = Number(amount) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const sym = currency || '$';
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${sign}${sym}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${sym}${abs.toFixed(0)}`;
}

export function formatPercent(p, digits = 1) {
  const n = Number(p) || 0;
  return `${(n * 100).toFixed(digits)}%`;
}

export function formatDate(iso, locale = 'zh-HK') {
  if (!iso) return '';
  // Treat YYYY-MM-DD as local date (don't get timezone-shifted).
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(locale, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export function formatDelta(p) {
  const n = Number(p) || 0;
  if (n === 0) return '—';
  // Phase 49j: when |delta| exceeds 100%, the percentage math is dominated
  // by small denominators (e.g. prior period $100 → current $1 = +900%).
  // Show a ">100%" cap so users don't see wild numbers and assume a bug.
  const capped = Math.abs(n) > 1 ? '>100%' : `${formatPercent(Math.abs(n))}`;
  const arrow = n > 0 ? '↑' : '↓';
  return `${arrow} ${capped}`;
}

// Phase 77g: percentage-point delta (e.g. savings rate moved from
// 80% → 85% = +5pp). Same arrow convention as formatDelta, but the
// number is signed in pp (no fractional cap) and labeled with "pp".
export function formatDeltaPp(pp) {
  const n = Number(pp);
  if (!Number.isFinite(n) || n === 0) return '—';
  const arrow = n > 0 ? '↑' : '↓';
  const abs = Math.abs(n);
  // Savings rate deltas are usually small (<10pp); show 1 decimal.
  return `${arrow} ${abs.toFixed(1)}pp`;
}

export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ymKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function ymdKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function previousMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return ymKey(d);
}

export function nextMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m, 1);
  return ymKey(d);
}

export function shiftYmd(ymd, delta) {
  // ymd: 'YYYY-MM-DD' -> shifted by delta days (positive = forward)
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return ymdKey(dt);
}
